import {
  defaultCountries,
  getCountry,
  guessCountryByPartialPhoneNumber,
} from "react-international-phone";

export const parseMultiSelectStr = (s) =>
  Array.isArray(s) ? s : String(s || "").split(",").map((x) => x.trim()).filter(Boolean);

export const toCommaSeparated = (val) =>
  Array.isArray(val) ? val.join(",") : (val != null && val !== "" ? String(val) : "");

/** Map form value to API format (Bronze/Silver/Gold/Platinum). Rejects invalid values like "on". */
export const toApiSponsorshipProgram = (v) => {
  const s = (v || "").toString().trim().toLowerCase();
  const map = { bronze: "Bronze", silver: "Silver", gold: "Gold", platinum: "Platinum" };
  return map[s] || null;
};

/** Normalize API value (any case) to form value (bronze/silver/gold/platinum) */
export const normalizeSponsorshipForForm = (v) => {
  const s = (v || "").toString().trim().toLowerCase();
  return ["bronze", "silver", "gold", "platinum"].includes(s) ? s : "";
};

export const isPartnerLikeRole = (r) => r === "ecosystem_partner" || r === "corporate_sponsor";

/**
 * Split a stored E.164 number into the (countryCode, nationalNumber) pair the
 * forms hold.
 *
 * Backed by the library's full country list. A hardcoded 4-entry table here used
 * to fall back to US for every other country, so a stored German number came back
 * as {US, "4917012345678"} — the phone input then rendered it under the US mask,
 * truncated it, and the user saw a stranger's number in their own profile.
 */
export const parseE164Phone = (phoneStr) => {
  const s = String(phoneStr || "").trim();
  if (!s.startsWith("+")) return null;
  const digits = s.slice(1).replace(/\D/g, "");
  if (!digits.length) return null;

  const { country } = guessCountryByPartialPhoneNumber({ phone: `+${digits}` });
  const dialCode = country?.dialCode;

  if (dialCode && digits.startsWith(dialCode) && digits.length > dialCode.length) {
    return {
      // The forms use legacy "UK" rather than ISO "GB" in places; PhoneInput maps
      // both, so returning the library's ISO code is safe.
      countryCode: String(country.iso2).toUpperCase(),
      nationalNumber: digits.slice(dialCode.length),
    };
  }

  return { countryCode: "US", nationalNumber: digits };
};

export const countryCodeToDialPrefix = { US: "+1", CA: "+1", UK: "+44", GB: "+44", AU: "+61", IN: "+91" };

/** Rejoin a (countryCode, nationalDigits) pair into E.164, using the full country list. */
export const buildE164Phone = (countryCode, nationalDigits) => {
  const digits = String(nationalDigits || "").replace(/\D/g, "");
  if (!digits) return "";

  const iso2 = String(countryCode || "US").toUpperCase() === "UK" ? "gb" : String(countryCode || "US").toLowerCase();
  const country = getCountry({ field: "iso2", value: iso2, countries: defaultCountries });
  const dialCode = country?.dialCode;
  if (dialCode) return `+${dialCode}${digits}`;

  // Unknown country code: fall back to the legacy table rather than guessing.
  return (countryCodeToDialPrefix[countryCode] || countryCodeToDialPrefix.US) + digits;
};
